/**
 * Player Image URL Generator
 * 
 * Attempts to get player images from multiple sources:
 * 1. NBA CDN (official photos)
 * 2. 2K Ratings website
 * 3. Fallback to initials-based avatar component
 */

// NBA API player ID mapping (mapping real NBA player names to their IDs)
// This would ideally come from a database, but we'll use a sample mapping
const NBA_PLAYER_IDS = {
  // Sample mappings - in production, you'd have a complete database
  'lebron-james': '2544',
  'stephen-curry': '201939',
  'kevin-durant': '201142',
  'giannis-antetokounmpo': '203507',
  'nikola-jokic': '203999',
  'joel-embiid': '203954',
  'luka-doncic': '1629029',
  'jayson-tatum': '1628369',
  // Add more as needed
};

/**
 * Get NBA CDN image URL for a player
 * @param {Object} player - Player object with firstName and lastName
 * @returns {string|null} - NBA CDN image URL or null
 */
export const getNBAImageUrl = (player) => {
  if (!player || !player.firstName || !player.lastName) return null;
  
  const playerKey = `${player.firstName}-${player.lastName}`.toLowerCase().replace(/\s+/g, '-');
  const playerId = NBA_PLAYER_IDS[playerKey];
  
  if (playerId) {
    return `https://cdn.nba.com/headshots/nba/latest/1040x760/${playerId}.png`;
  }
  
  return null;
};

/**
 * Get 2K Ratings image URL for a player
 * @param {Object} player - Player object with firstName and lastName
 * @returns {string} - 2K Ratings image URL
 */
export const get2KRatingsImageUrl = (player) => {
  if (!player || !player.firstName || !player.lastName) return null;
  
  const playerSlug = `${player.firstName}-${player.lastName}`.toLowerCase().replace(/\s+/g, '-');
  // 2K Ratings might have player images in their WordPress uploads
  return `https://www.2kratings.com/wp-content/uploads/${playerSlug}.png`;
};

/**
 * Get player initials for avatar fallback
 * @param {Object} player - Player object with firstName and lastName
 * @returns {string} - Player initials
 */
export const getPlayerInitials = (player) => {
  if (!player) return '?';
  
  const firstName = player.firstName || '';
  const lastName = player.lastName || '';
  
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

/**
 * Get team color for avatar background
 * @param {Object} team - Team object
 * @returns {string} - CSS color value
 */
export const getTeamColor = (team) => {
  if (!team) return '#1d428a'; // Default NBA blue
  
  // NBA team colors
  const teamColors = {
    // Atlantic
    'Celtics': '#007A33',
    '76ers': '#006BB6',
    'Nets': '#000000',
    'Knicks': '#006BB6',
    'Raptors': '#CE1141',
    
    // Central
    'Bucks': '#00471B',
    'Bulls': '#CE1141',
    'Cavaliers': '#860038',
    'Pistons': '#C8102E',
    'Pacers': '#002D62',
    
    // Southeast
    'Heat': '#98002E',
    'Hawks': '#E03A3E',
    'Hornets': '#1D1160',
    'Magic': '#0077C0',
    'Wizards': '#002B5C',
    
    // Northwest
    'Nuggets': '#0E2240',
    'Trail Blazers': '#E03A3E',
    'Thunder': '#007AC1',
    'Jazz': '#002B5C',
    'Timberwolves': '#0C2340',
    
    // Pacific
    'Warriors': '#1D428A',
    'Clippers': '#C8102E',
    'Lakers': '#552583',
    'Suns': '#1D1160',
    'Kings': '#5A2D81',
    
    // Southwest
    'Mavericks': '#00538C',
    'Rockets': '#CE1141',
    'Grizzlies': '#5D76A9',
    'Pelicans': '#0C2340',
    'Spurs': '#C4CED4',
  };
  
  return teamColors[team.name] || '#1d428a';
};

/**
 * Main function to get player image with fallbacks
 * @param {Object} player - Player object
 * @param {string} size - 'small' (64px) or 'large' (160px)
 * @returns {Object} - { type: 'url' | 'initials', value: string, color: string }
 */
export const getPlayerImage = (player, size = 'small') => {
  const nbaUrl = getNBAImageUrl(player);
  
  if (nbaUrl) {
    return {
      type: 'url',
      value: nbaUrl,
      fallbackUrls: [get2KRatingsImageUrl(player)]
    };
  }
  
  // Fallback to 2K Ratings
  const ratingsUrl = get2KRatingsImageUrl(player);
  if (ratingsUrl) {
    return {
      type: 'url',
      value: ratingsUrl,
      fallbackUrls: []
    };
  }
  
  // Final fallback to initials
  return {
    type: 'initials',
    value: getPlayerInitials(player),
    color: getTeamColor(player.team)
  };
};
